import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';
import '../styles/components/profileTabs.css';

interface UserStats {
  item_count: number;
  photo_count: number;
  storage_bytes: number;
  db_storage_bytes: number;
}

const UserStatsTab: React.FC = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!token) return;
      
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(API_ENDPOINTS.GET_USER_STATS, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        } else {
          setError('Failed to load statistics');
        }
      } catch (err) {
        console.error('Failed to fetch user stats:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [token]);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  return (
    <div className="profile-tab-content" style={{ padding: 0, margin: 0, maxWidth: 'none' }}>
      <div className="profile-card" style={{ margin: '0 8px 12px 8px' }}>
        {loading ? (
          <div className="stats-loading">Loading statistics...</div>
        ) : error ? (
          <div className="stats-error">{error}</div>
        ) : stats ? (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '12px',
            padding: '12px',
            background: 'var(--color-bg-secondary)',
            borderRadius: '12px',
            border: '1px solid var(--color-border)'
          }}>
              <div className="stat-compact-item" style={{ alignItems: 'flex-end', minWidth: '90px' }}>
                <span className="stat-compact-value" style={{ color: '#8b5cf6' }}>{stats.item_count.toLocaleString()}</span>
                <span className="stat-compact-label">Items</span>
              </div>
              <div className="stat-compact-item" style={{ alignItems: 'flex-end', minWidth: '90px' }}>
                <span className="stat-compact-value" style={{ color: '#ec4899' }}>{stats.photo_count.toLocaleString()}</span>
                <span className="stat-compact-label">Photos</span>
              </div>
              <div className="stat-compact-item" style={{ alignItems: 'flex-end', minWidth: '90px' }}>
                <span className="stat-compact-value-small" style={{ color: 'var(--color-success)' }}>{formatBytes(stats.storage_bytes)}</span>
                <span className="stat-compact-label">Photo Storage</span>
              </div>
              <div className="stat-compact-item" style={{ alignItems: 'flex-end', minWidth: '90px' }}>
                <span className="stat-compact-value-small" style={{ color: 'var(--color-warning)' }}>{formatBytes(stats.db_storage_bytes)}</span>
                <span className="stat-compact-label">DB Storage</span>
              </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UserStatsTab;
